import { normalizeHex } from "./harmonicPalette";
import {
  findTrimmedRunLengthRepeat,
  smallestCellPeriodLength,
  smallestStrictPeriodLength,
} from "./repeatDetectRunGroups";

export function isEmptyBead(color, defaultColor) {
  if (color == null || color === "") return true;
  const canon = normalizeHex(color) || String(color).trim().toLowerCase();
  const def =
    normalizeHex(defaultColor) ||
    String(defaultColor || "")
      .trim()
      .toLowerCase();
  return !canon || canon === def;
}

export function groupConsecutiveColors(colors) {
  const groups = [];
  for (const color of colors) {
    const last = groups[groups.length - 1];
    if (last && last[0] === color) last[1] += 1;
    else groups.push([color, 1]);
  }
  return groups;
}

/** How many chart rows at the top have no colored beads. */
export function emptyRowsFromTop(pattern, cols, rows, defaultColor) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const total = c * r;
  const arr = Array.isArray(pattern) ? pattern : [];

  for (let row = 0; row < r; row++) {
    const high = total - row * c;
    for (let k = 0; k < c; k++) {
      if (!isEmptyBead(arr[high - k - 1], defaultColor)) return row;
    }
  }
  return r;
}

/**
 * Slide every bead up so the first filled row becomes the top row.
 * Empty rows that were above the motif are discarded.
 */
export function shiftPatternUp(pattern, cols, rows, defaultColor) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const total = c * r;
  const arr = Array.isArray(pattern) ? pattern : [];
  const next = Array.from(
    { length: total },
    (_, i) => arr[i] ?? defaultColor
  );

  const shift = emptyRowsFromTop(next, c, r, defaultColor);
  if (shift <= 0 || shift >= r) return next;

  const shifted = Array.from({ length: total }, () => defaultColor);
  for (let bead = 1; bead <= total; bead++) {
    const dest = bead + shift * c;
    if (dest <= total) shifted[dest - 1] = next[bead - 1];
  }
  return shifted;
}

/**
 * Colored beads in the first `throughRows` chart rows, counted in stringing
 * order. That is the bead where a dissolve appended after those rows begins.
 */
export function coloredBeadsBeforeRow(
  pattern,
  cols,
  rows,
  throughRows,
  defaultColor
) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const keep = Math.max(
    0,
    Math.min(Math.floor(Number(throughRows)) || 0, r)
  );
  const total = c * r;
  const arr = Array.isArray(pattern) ? pattern : [];
  const firstKept = total - keep * c + 1;
  let count = 0;
  for (let bead = total; bead >= firstKept; bead--) {
    if (!isEmptyBead(arr[bead - 1], defaultColor)) count += 1;
  }
  return count;
}

/**
 * Split run-length groups so a dissolve line can sit on a bead boundary.
 * Returns the runs before that bead and the runs after it.
 */
export function splitRunsAtBead(runs, beadIndex) {
  const list = Array.isArray(runs) ? runs : [];
  const at = Math.floor(Number(beadIndex));
  if (!Number.isFinite(at) || at <= 0) return [list, []];

  const before = [];
  const after = [];
  let seen = 0;
  let crossed = false;
  for (const run of list) {
    const count = Math.max(0, Number(run?.count) || 0);
    if (crossed || count === 0) {
      if (count > 0) after.push(run);
      continue;
    }
    const next = seen + count;
    if (next <= at) {
      before.push(run);
      seen = next;
      if (seen === at) crossed = true;
    } else if (seen >= at) {
      after.push(run);
      crossed = true;
    } else {
      const keep = at - seen;
      before.push({ ...run, count: keep });
      after.push({ ...run, count: count - keep });
      crossed = true;
    }
  }
  return [before, after];
}

/** Colored beads in stringing order, starting at the first filled square. */
export function coloredBeadsFromStringingStart(
  pattern,
  cols,
  rows,
  defaultColor
) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const total = c * r;
  const arr = Array.isArray(pattern) ? pattern : [];
  const colors = [];

  for (let bead = total; bead >= 1; bead--) {
    const color = arr[bead - 1];
    if (isEmptyBead(color, defaultColor)) continue;
    colors.push(color);
  }
  return colors;
}

/** One repeating unit in stringing order, from the first filled square. */
export function detectStringingSequence(pattern, cols, rows, defaultColor) {
  const colors = coloredBeadsFromStringingStart(
    pattern,
    cols,
    rows,
    defaultColor
  );
  if (colors.length === 0) return null;

  // Prefer the bead-level period. Run-length detection misses patterns like
  // herringbone where a partial second cycle merges runs across the seam, so
  // the group period looks like the whole chart even though cells repeat.
  const cellPeriod = smallestCellPeriodLength(colors);
  if (cellPeriod > 0 && cellPeriod < colors.length) {
    return colors.slice(0, cellPeriod);
  }

  const groups = groupConsecutiveColors(colors);
  if (groups.length === 0) return null;

  const strictSize = smallestStrictPeriodLength(groups);
  const repeatGroups =
    strictSize < groups.length
      ? groups.slice(0, strictSize)
      : findTrimmedRunLengthRepeat(groups)?.templateGroups ?? groups;

  return repeatGroups.flatMap(([color, count]) =>
    Array.from({ length: count }, () => color)
  );
}

/**
 * Shift empty top rows away, then tile `sequence` from the first filled
 * square downward through the rest of the chart.
 */
export function fillFromStringingStart(
  pattern,
  sequence,
  cols,
  rows,
  defaultColor
) {
  const c = Math.max(1, Math.floor(Number(cols)) || 1);
  const r = Math.max(0, Math.floor(Number(rows)) || 0);
  const total = c * r;
  const shifted = shiftPatternUp(pattern, c, r, defaultColor);

  if (!sequence || sequence.length === 0) return shifted;

  const next = shifted.slice();
  let started = false;
  let si = 0;
  for (let bead = total; bead >= 1; bead--) {
    if (!started) {
      if (isEmptyBead(shifted[bead - 1], defaultColor)) continue;
      started = true;
    }
    next[bead - 1] = sequence[si % sequence.length];
    si++;
  }
  return next;
}
